import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'

interface User {
  id: number
  spotify_id: string
  email: string
  display_name: string
  profile_image_url: string
  theme_preference?: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const refreshPromise = ref<Promise<void> | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  const setToken = (newToken: string): Promise<void> => {
    token.value = newToken
    localStorage.setItem('auth_token', newToken)
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
    // Also fetch user immediately after setting token
    return fetchUser()
  }

  const clearToken = () => {
    token.value = null
    user.value = null
    localStorage.removeItem('auth_token')
    delete api.defaults.headers.common['Authorization']
  }

  const refreshToken = async (): Promise<void> => {
    // Prevent multiple simultaneous refresh attempts
    if (refreshPromise.value) {
      return refreshPromise.value
    }

    refreshPromise.value = (async () => {
      try {
        // Get the current user to access their refresh token
        const currentUser = user.value
        if (!currentUser) {
          throw new Error('No user available for token refresh')
        }

        // For now, we'll just logout and require re-authentication
        // since we don't store the Spotify refresh token in frontend
        console.log('Token expired, redirecting to login...')
        clearToken()
        window.location.href = '/api/v1/auth/login'
      } catch (error) {
        console.error('Token refresh failed:', error)
        clearToken()
        window.location.href = '/api/v1/auth/login'
      } finally {
        refreshPromise.value = null
      }
    })()

    return refreshPromise.value
  }

  const fetchUser = async (): Promise<void> => {
    if (!token.value) return

    try {
      isLoading.value = true
      const response = await api.get('/v1/auth/me')
      user.value = response.data
      
      // Initialize theme from user preference
      if (user.value?.theme_preference) {
        const { useThemeStore } = await import('./theme')
        const themeStore = useThemeStore()
        themeStore.initializeTheme(user.value.theme_preference as 'light' | 'dark' | 'system')
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Try to refresh token, or logout if refresh fails
        await refreshToken()
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    clearToken()
  }

  // Initialize API headers if token exists
  if (token.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
    fetchUser()
  }

  return {
    token,
    user,
    isLoading,
    isAuthenticated,
    setToken,
    clearToken,
    fetchUser,
    logout
  }
})
